<template>
  <div :class="controlClasses" class="mds-form-control mds-date-range-input">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="$slots['icon']">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <div ref="calendar-wrap" class="date-value" :class="dateValueClasses">
          <template v-if="showInputLayoutString">
            <input
              ref="date-common-input"
              :value="selectedDate"
              :placeholder="placeholder"
              @input="onPlaceholderInputChange"
              @focus="showCalendar"
            />
          </template>

          <template v-if="showInputLayoutInput">
            с
            <VInputMask
              ref="date-from-input"
              :disabled="disabled"
              :clearable="clearable"
              :placeholder="placeholderDateFrom"
              :mask="dateMask"
              :value="internalRange.start?.toLocalString() ?? ''"
              raw
              @focus="showCalendar"
              @input="onInput($event, 'dateFrom')"
            >
              <template #icon>
                <VIcon
                  class="calendar-icon"
                  material="calendar_today"
                  @click="onClickIcon"
                />
              </template>
            </VInputMask>
            до
            <VInputMask
              ref="date-to-input"
              :disabled="disabled"
              :clearable="clearable"
              :placeholder="placeholderDateTo"
              :mask="dateMask"
              :value="internalRange.end?.toLocalString() ?? ''"
              raw
              @focus="showCalendar"
              @input="onInput($event, 'dateTo')"
            >
              <template #icon>
                <VIcon
                  class="calendar-icon"
                  material="calendar_today"
                  @click="onClickIcon"
                />
              </template>
            </VInputMask>
          </template>
        </div>

        <button
          v-if="showClear"
          class="clear"
          type="button"
          tabindex="-1"
          @click.stop="onClear"
        >
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="$slots['helper-text']">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>

      <VPopup
        class="mds-date-range-input-popup"
        :offset="4"
        :target="$refs['calendar-wrap']"
        :visible="calendarVisible"
        @update:visible="this.toggleCalendar"
        no-pointer
        @click.stop
      >
        <div class="mds-date-range-input__calendar">
          <div
            v-if="showCalendarControls"
            class="mds-date-range-input__calendar__form"
          >
            <div class="presets">
              <button
                :class="{ active: isPresetActive(preset) }"
                :key="preset.id"
                @click="onPresetClick(preset)"
                type="button"
                tabindex="-1"
                v-for="preset of presets"
              >
                {{ preset.label }}
              </button>
            </div>

            <div class="inputs">
              <div class="inputs-item">
                <label>Дата от</label>
                <VInputMask
                  ref="popup-date-from-input"
                  :disabled="disabled"
                  :clearable="clearable"
                  :placeholder="placeholderDateFrom"
                  :mask="dateMask"
                  :value="internalRange.start?.toLocalString() ?? ''"
                  raw
                  @input="onInput($event, 'dateFrom')"
                />
              </div>

              <div class="inputs-item">
                <label>Дата до</label>
                <VInputMask
                  ref="popup-date-to-input"
                  :disabled="disabled"
                  :clearable="clearable"
                  :placeholder="placeholderDateTo"
                  :mask="dateMask"
                  :value="internalRange.end?.toLocalString() ?? ''"
                  raw
                  @input="onInput($event, 'dateTo')"
                />
              </div>
            </div>

            <div class="buttons">
              <button class="mds-btn" @click="onClear">Очистить</button>
            </div>
          </div>

          <VMonthRenderer
            v-for="(_, index) of Array(showMonths)"
            :key="index"
            :first-day-of-week="0"
            :is-first="index === 0"
            :is-in-range="isInRange"
            :is-disabled="isDisabled"
            :is-selected="isSelected"
            :is-last="index + 1 === showMonths"
            :month="dataForMonthRender[index].month"
            :year="dataForMonthRender[index].year"
            @change-month="onChangeMonth"
            @mouse-enter-date="onMouseEnterDate"
            @mouse-leave-date="onMouseLeaveDate"
            @select-day="onSelectDay"
            @wheel="onMonthScroll"
          />
        </div>
        <div class="popup-helper-text" v-if="$slots['popup-helper-text']">
          <!-- @slot Слот вспомогательного текста для попап календаря -->
          <slot name="popup-helper-text" />
        </div>
      </VPopup>
    </div>
  </div>
</template>

<script lang="ts">
import DateSimple   from '@/core/types/DateSimple';
import DateRange    from '@/core/types/DateRange';
import VBaseControl from "@/core/services/VBaseControl";

import VMonthRenderer from "@/lib/components/VMonthRenderer/VMonthRenderer.vue";
import VPopup         from "@/lib/components/VPopup/VPopup.vue";
import VInputMask     from "@/lib/components/VInputMask/VInputMask.vue";
import VIcon          from "@/lib/components/VIcon/VIcon.vue";
import {UnwrapNestedRefs} from "vue";

const DATE_RANGE_INPUT_LAYOUTS = ["string", "input"] as const;

export default defineComponent({
  name: "VDateRangeInputNew",

  components: { VPopup, VInputMask, VMonthRenderer, VIcon },

  extends: VBaseControl,

  emits: ['input', 'update:modelValue', 'blur'],

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model. DateRange либо null. */
    modelValue: {
      type:    Object as PropType<DateRange | UnwrapNestedRefs<DateRange>>,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: null,
    },
    /** Количество месяцев в попапе */
    showMonths: {
      type:    Number,
      default: 2,
    },
    /** Выравнивание текста */
    textAlign: {
      type:    String,
      default: null,
      validate(value: string) {
        return ["left", "center", "right"].includes(value);
      },
    },
    showCalendarControls: {
      type:    Boolean,
      default: false,
    },
    inputLayout: {
      type:    String,
      default: "string",
      validate: (v: string) => DATE_RANGE_INPUT_LAYOUTS.includes(v as any),
    },
  },

  data() {
    return {
      internalRange:   new DateRange() as DateRange,
      preSelectedDate: null as Date | null,
      visibleDate:     null as Date | null,
      calendarVisible: false,
      presets: [
        { label: "Сегодня",        id: "today"      },
        { label: "Вчера",          id: "yesterday"  },
        { label: "Эта неделя",     id: "thisWeek"   },
        { label: "Прошлая неделя", id: "lastWeek"   },
        { label: "Этот месяц",     id: "thisMonth"  },
        { label: "Прошлый месяц",  id: "lastMonth"  },
      ],
    };
  },

  computed: {
    dateMask(): string {
      return this.$core.utils.date.dateMask;
    },

    placeholderDateFrom(): string {
      return this.placeholder === null ? "Дата от" : `${this.placeholder}, от`;
    },

    placeholderDateTo(): string {
      return this.placeholder === null ? "Дата до" : `${this.placeholder}, до`;
    },

    showInputLayoutString(): boolean {
      return this.inputLayout === "string";
    },

    showInputLayoutInput(): boolean {
      return this.inputLayout === "input";
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true)             classes.push("clearable");
      if (this.showInputLayoutString === true) classes.push("layout-string");
      if (this.showInputLayoutInput  === true) classes.push("layout-input");
      return classes;
    },

    selectedDate(): string | null {
      return this.internalRange.isEmpty()
        ? null
        : this.internalRange.getStringRepresentation();
    },

    dateValueClasses(): string[] {
      const classes: string[] = [];
      if (this.calendarVisible === true) classes.push("focus");
      classes.push(`layout-${this.inputLayout}`);
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.internalRange.isEmpty() === false
        && this.disabled === false
        && this.inputLayout === "string";
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    dataForMonthRender(): { month: number; year: number }[] {
      return new Array(this.showMonths).fill(null).map((_item, index) => {
        const tempDate = new Date(this.visibleDate);
        tempDate.setMonth(tempDate.getMonth() + index);
        return { month: tempDate.getMonth(), year: tempDate.getFullYear() };
      });
    },
  },

  watch: {
    modelValue(newValue: DateRange | null) {
      this.internalRange = newValue instanceof DateRange ? newValue : new DateRange();
    },
  },

  created() {
    this.internalRange = this.modelValue instanceof DateRange ? this.modelValue : new DateRange();
    this.visibleDate   = this.internalRange.start ?? new Date();
  },

  methods: {
    /**
     * Централизованный эмит изменений.
     * Устанавливает dirty, обновляет internalRange, эмитит наружу и запускает валидацию.
     */
    emitChange(newRange: DateRange | null) {
      this.validation.markDirty();
      this.internalRange = newRange ?? new DateRange();
      this.$emit("input", newRange);
      this.$emit("update:modelValue", newRange);
      this.validate();
    },

    onClickIcon(event: MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      if (this.showInputLayoutString === true) {
        this.$refs["date-common-input"].focus();
      }
    },

    onChangeMonth(data: { direction: 'next' | 'prev' }) {
      const date = this.$core.utils.date.cloneDate(this.visibleDate);
      date.setMonth(date.getMonth() + (data.direction === "next" ? 1 : -1));
      this.visibleDate = date;
    },

    onSelectDay(date: Date) {
      const { start: dateFrom, end: dateTo } = this.internalRange;
      const newDateStart = new DateSimple(date);

      if (dateFrom === null || (dateFrom !== null && dateTo !== null)) {
        this.emitChange(new DateRange(newDateStart, null));
        return;
      }

      if (newDateStart.getTime() < dateFrom.getTime()) {
        this.emitChange(new DateRange(
          newDateStart,
          new DateSimple(this.$core.utils.date.toDayEnd(dateFrom)),
        ));
      } else {
        this.emitChange(new DateRange(
          dateFrom,
          new DateSimple(this.$core.utils.date.toDayEnd(date)),
        ));
      }

      if (this.internalRange.start !== null && this.internalRange.end !== null) {
        this.hideCalendar();
      }
    },

    onMouseEnterDate(date: Date) {
      this.preSelectedDate = date;
    },

    onMouseLeaveDate() {
      this.preSelectedDate = null;
    },

    isSelected(date: Date): boolean {
      const { start: dateFrom, end: dateTo } = this.internalRange;
      if (dateFrom !== null && dateTo !== null) {
        const fromTime = dateFrom.getTime();
        const toTime   = dateTo.getTime();
        const dateTime = this.$core.utils.date.toDayStart(date).getTime();
        return fromTime <= dateTime && dateTime <= toTime;
      }
      return this.$core.utils.date.format(dateFrom) === this.$core.utils.date.format(date);
    },

    isInRange(date: Date): boolean {
      const { start: dateFrom, end: dateTo } = this.internalRange;
      if (this.preSelectedDate === null || dateFrom === null || (dateFrom !== null && dateTo !== null)) {
        return false;
      }
      const fromTime = dateFrom.getTime();
      const toTime   = this.preSelectedDate.getTime();
      const dateTime = date.getTime();
      return (fromTime < dateTime && dateTime < toTime) || (fromTime > dateTime && dateTime > toTime);
    },

    isDisabled(date: Date): boolean | null {
      /**
       * Чтобы определить валидность даты — проверяем что она вписывается в рамки,
       * без оглядки на диапазоны.
       */
      if (this.validateMethod !== null) return this.validateMethod(date);
      return null;
    },

    onMonthScroll(event: WheelEvent) {
      event.preventDefault();
      const delta = event.deltaY || event.detail || (event as any).wheelDelta;
      this.onChangeMonth({ direction: delta > 0 ? "next" : "prev" });
    },

    onInput(event: any, field: 'dateFrom' | 'dateTo') {
      if (event.fullfilled === false) {
        if (event.masked.length === 0) {
          if (field === "dateFrom") {
            this.emitChange(new DateRange(null, this.internalRange.end));
          } else {
            this.emitChange(new DateRange(this.internalRange.start, null));
          }
        }
        return;
      }

      const newDate = this.$core.utils.date.createFromInputString(event.masked);
      if (field === "dateFrom") {
        this.emitChange(new DateRange(newDate, this.internalRange.end));
      } else {
        if (this.internalRange.start !== null && newDate < this.internalRange.start) {
          this.emitChange(new DateRange(newDate, this.internalRange.start));
        } else {
          this.emitChange(new DateRange(this.internalRange.start, newDate));
        }
      }

      if (this.internalRange.start !== null && this.internalRange.end !== null) {
        this.hideCalendar();
      }
    },

    onClear() {
      this.emitChange(null);
      this.$emit("blur", null);
      this.toggleCalendar(false);
    },

    showCalendar() {
      if (this.disabled === true) return;
      this.validation.markTouched();
      this.toggleCalendar(true);
      window.addEventListener("keydown", this.keyDownListeners);
    },

    hideCalendar() {
      this.toggleCalendar(false);
      this.$emit("blur", null);
      window.removeEventListener("keydown", this.keyDownListeners);
    },

    toggleCalendar(value: boolean) {
      this.calendarVisible = value;
      if (value === true) {
        this.visibleDate = this.internalRange.start ?? new Date();
      }
    },

    isPresetActive(preset: { id: string }): boolean {
      const { start: dateFrom, end: dateTo } = this.internalRange;
      if (dateFrom === null || dateTo === null) return false;
      const presetRange = this.calculatePresetDateRange(preset.id);
      return (
        this.$core.utils.date.format(dateFrom) === this.$core.utils.date.format(presetRange.start)
        && this.$core.utils.date.format(dateTo) === this.$core.utils.date.format(presetRange.end)
      );
    },

    onPresetClick(preset: { id: string }) {
      const presetRange = this.calculatePresetDateRange(preset.id);
      this.emitChange(new DateRange(
        this.$core.utils.date.toDayStart(presetRange.start),
        this.$core.utils.date.toDayEnd(presetRange.end),
      ));
      this.hideCalendar();
    },

    calculatePresetDateRange(id: string): DateRange {
      let period = new Date();
      let dateFrom: Date | null = null;
      let dateTo:   Date | null = null;

      switch (id) {
        case "today":
          dateFrom = dateTo = period;
          break;
        case "yesterday":
          period.setDate(period.getDate() - 1);
          dateFrom = dateTo = period;
          break;
        case "thisWeek":
          dateFrom = this.$core.utils.date.getCurrentWeekStartDate();
          dateTo = new Date(dateFrom);
          dateTo.setDate(dateTo.getDate() + 6);
          break;
        case "lastWeek":
          period = this.$core.utils.date.getCurrentWeekStartDate();
          period.setDate(period.getDate() - 7);
          dateFrom = new Date(period);
          dateTo = new Date(period);
          dateTo.setDate(dateTo.getDate() + 6);
          break;
        case "thisMonth":
          dateFrom = new Date(period.getFullYear(), period.getMonth(), 1);
          dateTo   = new Date(period.getFullYear(), period.getMonth() + 1, 0);
          break;
        case "lastMonth":
          dateFrom = new Date(period.getFullYear(), period.getMonth() - 1, 1);
          dateTo   = new Date(period.getFullYear(), period.getMonth(), 0);
          break;
      }
      return new DateRange(dateFrom, dateTo);
    },

    isDateDisabled(date: Date): boolean {
      return this.validateMethod !== null
        ? this.validateMethod(date) !== null
        : false;
    },

    isRangeContainsDisabledDate(dateFrom: Date | null, dateTo: Date | null): boolean {
      if (this.validateMethod === null || dateFrom === null || dateTo === null) return false;
      let currentDate = new Date(dateFrom);
      while (currentDate <= dateTo) {
        if (this.isDateDisabled(currentDate) === true) return true;
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return false;
    },

    /**
     * Запускает валидацию текущего диапазона через Validation.
     * Доп. бизнес-правило (диапазон содержит недоступные даты) применяем поверх.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      const { start: dateFrom, end: dateTo } = this.internalRange;
      await this.validation.validate({ dateFrom, dateTo });

      if (this.required && this.dateRangeIsNull(dateFrom, dateTo)) {
        this.validation.errors.push(this.$core.utils.validators.MESSAGES.required);
        this.validation.isValid = false;
      }

      if (this.isRangeContainsDisabledDate(dateFrom, dateTo) === true) {
        this.validation.errors.push("Выбранный период содержит недоступные даты");
        this.validation.isValid = false;
      }
    },

    dateRangeIsNull(dateFrom, dateTo): boolean {
      if (dateFrom === null && dateTo === null) {
        return true;
      }
      return false;
    },

    onPlaceholderInputChange(event: Event) {
      const input = event.target as HTMLInputElement;
      input.value = this.selectedDate;
    },

    keyDownListeners(event: KeyboardEvent) {
      switch (event.key) {
        case "Tab": {
          const popupDateFromInput = this.$refs["popup-date-from-input"]
          && this.$refs["popup-date-from-input"].hasOwnProperty("$el")
            ? this.$refs["popup-date-from-input"].$el.querySelector("input")
            : null;
          const popupDateToInput = this.$refs["popup-date-to-input"]
          && this.$refs["popup-date-to-input"].hasOwnProperty("$el")
            ? this.$refs["popup-date-to-input"].$el.querySelector("input")
            : null;
          const input = document.activeElement;

          if (
            (popupDateFromInput !== null && input === popupDateFromInput)
            || (popupDateToInput !== null && input === popupDateToInput)
          ) {
            return;
          }

          if (this.showInputLayoutString === true) {
            this.hideCalendar();
          } else if (this.showInputLayoutInput === true) {
            const dateToInput = this.$refs["date-to-input"]
            && this.$refs["date-to-input"].hasOwnProperty("$el")
              ? this.$refs["date-to-input"].$el.querySelector("input")
              : null;
            if (dateToInput !== null && input === dateToInput) {
              this.hideCalendar();
            }
          }
          break;
        }
        case "Escape": {
          if (this.calendarVisible === true) {
            const input = document.activeElement as HTMLElement | null;
            if (input && typeof input.blur === "function") input.blur();
            this.hideCalendar();
          }
          break;
        }
      }
    },
  },
});
</script>
